import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { useState, useEffect } from 'react';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Scale, Calendar, FileText, Users, Gavel, ArrowUpDown, HelpCircle } from 'lucide-react';
import { judgmentsReadJudgment, judgmentsGetJudgmentClassification } from '@/client';
import type { JudgmentPublic, JudgmentClassificationResponse } from '@/client';
import useAuth from '@/hooks/useAuth';
import FeedbackForm from '@/components/FeedbackForm';
import { AppLayout } from "@/components/layout/AppLayout";

const AvgorandeDetail = () => {
  const { judgmentId } = useParams<{ judgmentId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuth();
  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [pdfError, setPdfError] = useState<string | null>(null);

  // Fetch judgment details from backend API
  const { data: judgment, isLoading: judgmentLoading, error: judgmentError } = useQuery({
    queryKey: ["judgment", judgmentId],
    queryFn: () => judgmentsReadJudgment({ judgmentId: judgmentId! }),
    enabled: !!user && !!judgmentId,
  });

  // Fetch judgment classification data
  const { data: classification, isLoading: classificationLoading, error: classificationError } = useQuery({
    queryKey: ["judgment-classification", judgmentId],
    queryFn: () => judgmentsGetJudgmentClassification({ judgmentId: judgmentId! }),
    enabled: !!user && !!judgmentId,
  });

  // Fetch PDF file as blob when judgment is loaded
  useEffect(() => {
    const fetchPdf = async () => {
      if (!judgment?.id || !user) return;

      try {
        setPdfError(null);
        const token = localStorage.getItem("access_token");
        
        const response = await fetch(`${import.meta.env.VITE_API_URL || "http://localhost:8000"}/api/v1/judgments/${judgment.id}/file`, {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        });

        if (!response.ok) {
          throw new Error(`Failed to load PDF: ${response.status}`);
        }

        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (err) {
        console.error('Failed to load PDF:', err);
        setPdfError(err instanceof Error ? err.message : 'Failed to load PDF');
      }
    };

    fetchPdf();

    // Cleanup function to revoke the object URL
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [judgment?.id, user]); // eslint-disable-line react-hooks/exhaustive-deps

  const loading = judgmentLoading || classificationLoading;
  const error = judgmentError || classificationError;

  const formatDate = (dateStr: string) => {
    return new Date(dateStr).toLocaleDateString('sv-SE');
  };

  if (loading) {
    return (
      <AppLayout>
        <div className="flex flex-1 items-center justify-center">
          <div className="text-center">
            <div className="mx-auto mb-4 h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent"></div>
            <p className="text-muted-foreground">Laddar avgörande...</p>
          </div>
        </div>
      </AppLayout>
    );
  }

  if (error || !judgment || !classification) {
    return (
      <AppLayout>
        <div className="flex flex-1 items-center justify-center">
          <div className="space-y-4 text-center">
            <p className="text-red-500">Avgörandet kunde inte laddas</p>
            <Button onClick={() => navigate('/judgements')} variant="outline">
              Gå till avgöranden
            </Button>
          </div>
        </div>
      </AppLayout>
    );
  }

  const classificationData = classification.classification;
  const legalProvisions = classification.legal_provisions || [];

  return (
    <AppLayout onBack={() => navigate(-1)}>
      <div className="mx-auto grid w-full max-w-7xl grid-cols-1 gap-6 lg:grid-cols-2">
          {/* PDF Viewer - Left Side */}
          <Card className="p-0 overflow-hidden bg-gradient-card shadow-card-legal border border-primary/20">
            <div className="bg-gradient-header text-primary-foreground p-4">
              <div className="flex items-center space-x-2">
                <FileText className="h-5 w-5 text-primary-foreground" />
                <h3 className="font-semibold tracking-wide uppercase">Domslut</h3>
              </div>
            </div>
            <div className="h-96 lg:h-[800px]">
              {pdfError ? (
                <div className="flex items-center justify-center h-full bg-muted/10">
                  <div className="text-center">
                    <FileText className="h-12 w-12 mx-auto mb-4 text-red-500" />
                    <p className="text-red-500 mb-2">Kunde inte ladda PDF</p>
                    <p className="text-sm text-muted-foreground">{pdfError}</p>
                  </div>
                </div>
              ) : pdfUrl ? (
                <iframe
                  src={pdfUrl}
                  className="w-full h-full border-0 bg-white"
                  title="Domslut PDF"
                />
              ) : (
                <div className="flex items-center justify-center h-full bg-muted/10">
                  <div className="text-center">
                    <div className="animate-spin h-8 w-8 border-2 border-primary border-t-transparent rounded-full mx-auto mb-4"></div>
                    <p className="text-muted-foreground">Laddar PDF...</p>
                  </div>
                </div>
              )}
            </div>
          </Card>

          {/* Analysis Data - Right Side */}
          <div className="space-y-6">
            {/* Case Overview */}
            <Card className="p-6 bg-gradient-card shadow-card-legal border border-primary/20">
              <div className="flex items-center space-x-2 mb-4">
                <Gavel className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold text-foreground">Målöversikt</h3>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Domstol</span>
                    <p className="text-foreground">{classificationData?.domstol || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Målnummer</span>
                    <p className="text-foreground">{classificationData?.målnummer || 'N/A'}</p>
                  </div>
                  <div>
                    <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Datum</span>
                    <p className="text-foreground">{classificationData?.dom_datum ? formatDate(classificationData.dom_datum) : formatDate(judgment.upload_date)}</p>
                  </div>
                </div>
                
                <div>
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Parter</span>
                  <p className="text-foreground">{classificationData?.parter || 'N/A'}</p>
                </div>
                
                <div>
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Ersättningsmodell</span>
                  <p className="text-foreground">{classificationData?.ersattningsmodell || 'N/A'}</p>
                </div>
                
                {classificationData?.converted_from && (
                  <div>
                    <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Konverterad från</span>
                    <p className="text-foreground text-accent font-medium">{classificationData.converted_from}</p>
                  </div>
                )}
                
                
                <div>
                  <span className="text-sm font-semibold uppercase tracking-wide text-muted-foreground">Bakgrund</span>
                  <p className="text-foreground leading-relaxed">{classificationData?.bakgrund || 'Ingen bakgrund tillgänglig'}</p>
                </div>
              </div>
            </Card>

            {/* Prövade Bestämmelser */}
            <Card className="p-6 bg-gradient-card shadow-card-legal border border-primary/20">
              <div className="flex items-center space-x-2 mb-4">
                <FileText className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold text-foreground">Prövade bestämmelser</h3>
              </div>
              
              <div className="flex flex-wrap gap-2">
                {legalProvisions.length > 0 ? (
                  legalProvisions.map((provision) => (
                    <Badge 
                      key={provision.id} 
                      variant="outline" 
                      className="border border-primary/40 bg-primary/10 text-primary cursor-pointer transition-colors hover:border-primary hover:bg-primary hover:text-primary-foreground"
                      onClick={() => {
                        const element = document.getElementById(`provision-${provision.id}`);
                        if (element) {
                          element.scrollIntoView({ behavior: 'smooth', block: 'center' });
                          element.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
                          setTimeout(() => {
                            element.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
                          }, 2000);
                        }
                      }}
                    >
                      {provision.paragraf}
                    </Badge>
                  ))
                ) : (
                  <p className="text-muted-foreground">Inga prövade bestämmelser tillgängliga</p>
                )}
              </div>
            </Card>

            {/* Slutsats */}
            {classificationData?.slutsats && (
              <Card className="p-6 bg-gradient-card shadow-card-legal border border-primary/20">
                <div className="flex items-center space-x-2 mb-4">
                  <Gavel className="h-5 w-5 text-primary" />
                  <h3 className="text-xl font-semibold text-primary">Slutsats</h3>
                </div>
                
                <p className="text-foreground leading-relaxed text-base">
                  {classificationData.slutsats}
                </p>
              </Card>
            )}

            {/* Domstolens Bedömning */}
            <Card className="p-6 bg-gradient-card shadow-card-legal border border-primary/20">
              <div className="flex items-center space-x-2 mb-4">
                <Scale className="h-5 w-5 text-primary" />
                <h3 className="text-xl font-semibold text-foreground">Domstolens bedömning</h3>
              </div>
              
              <div className="space-y-4">
                {legalProvisions.length > 0 ? (
                  legalProvisions.map((provision) => (
                    <div 
                      key={provision.id} 
                      id={`provision-${provision.id}`}
                      className="border border-primary/25 bg-background/80 rounded-lg p-4 transition-all shadow-sm"
                    >
                      <div className="flex items-center space-x-2 mb-3">
                        <Badge variant="secondary" className="border border-primary/30 bg-primary/10 text-primary">
                          {provision.avtal || classificationData?.avtal_typ || 'AB04'} - {provision.paragraf}
                        </Badge>
                        {provision.converted_from && (
                          <Badge variant="outline" className="text-xs border-accent/60 text-accent">
                            {provision.converted_from}
                          </Badge>
                        )}
                      </div>
                      
                      {provision.frågeställning && (
                        <div className="mb-3">
                          <div className="flex items-center space-x-2 mb-1">
                            <HelpCircle className="h-4 w-4 text-primary" />
                            <h5 className="font-semibold text-sm text-muted-foreground">Frågeställning</h5>
                          </div>
                          <p className="text-foreground text-sm italic leading-relaxed">
                            {provision.frågeställning}
                          </p>
                        </div>
                      )}
                      
                      <Separator className="my-3" />
                      
                      <div>
                        <div className="flex items-center space-x-2 mb-1">
                          <Gavel className="h-4 w-4 text-primary" />
                          <h5 className="font-semibold text-sm text-muted-foreground">Bedömning</h5>
                        </div>
                        <p className="text-foreground leading-relaxed">
                          {provision.bedomning || 'Ingen bedömning tillgänglig'}
                        </p>
                      </div>
                      
                      {/* Citat section hidden */}
                    </div>
                  ))
                ) : (
                  <p className="text-muted-foreground">Inga bedömningar tillgängliga</p>
                )}
              </div>
            </Card>

            {/* Feedback Form */}
            <FeedbackForm judgmentId={judgment.id} />
          </div>
      </div>
    </AppLayout>
  );
};

export default AvgorandeDetail;
